/**
 * Dataset registry — the selectable read boundary (Decision 2). The Command Center is NOT hardwired to
 * one artifact: it enumerates every genuine Atlas-produced dataset in this checkout and lets a single
 * observed environment be selected. Selecting one makes the ENTIRE Command Center contextual to that
 * dataset — unrelated companies are never merged into one fabricated environment.
 *
 * Two kinds, honestly distinguished:
 *   - assessment: the canonical rich path (findings/controls/evidence/risk/certificate), bundled.
 *   - discovery:  ~150 real external-domain observations, lazily loaded on selection (code-split) so the
 *                 selector is instant and the initial bundle stays lean.
 *
 * Provenance is explicit on every entry; fixtures are never listed here.
 */

import acc001 from "./sample/acceptance-001.assessment.json";
import acc002 from "./sample/acceptance-002.assessment.json";
import type { Assessment } from "./assessment-source";
import type { DiscoverySnapshot } from "./snapshot-source";
import { DISCOVERY_INDEX, type DiscoveryIndexEntry } from "./data/discovery-index.generated";

export type DatasetKind = "assessment" | "discovery";

export interface DatasetRef {
  readonly id: string;
  readonly kind: DatasetKind;
  readonly label: string;
  /** Human-readable origin — real captured Atlas output, honestly attributed. */
  readonly provenance: string;
}

const ASSESSMENTS: readonly DatasetRef[] = [
  { id: "acceptance-002", kind: "assessment", label: "Build host — acceptance-002", provenance: "Atlas assessment engagement · on-host acceptance · assessment-engine/1.0.0 · generated 2026-08-13" },
  { id: "acceptance-001", kind: "assessment", label: "Build host — acceptance-001", provenance: "Atlas assessment engagement · on-host acceptance · earlier run" },
];

const DISCOVERY: readonly DatasetRef[] = DISCOVERY_INDEX.map((e) => ({
  id: e.id,
  kind: "discovery" as const,
  label: `${e.domain} — discovery`,
  provenance: `Atlas Discovery external-domain observation · public DNS/TLS/HTTP only · generated ${e.generatedAt.slice(0, 10)}`,
}));

export const DATASETS: readonly DatasetRef[] = [...ASSESSMENTS, ...DISCOVERY];
export { DISCOVERY_INDEX, type DiscoveryIndexEntry };
export const discoveryIndex = (id: string): DiscoveryIndexEntry | undefined => DISCOVERY_INDEX.find((e) => e.id === id);

export const DEFAULT_DATASET_ID = "acceptance-002";

export function datasetRef(id: string): DatasetRef | undefined {
  return DATASETS.find((d) => d.id === id);
}

export function loadAssessment(id: string): Assessment | null {
  if (id === "acceptance-002") return acc002 as unknown as Assessment;
  if (id === "acceptance-001") return acc001 as unknown as Assessment;
  return null;
}

/** Lazy per-target loaders — each snapshot is its own async chunk, loaded only when selected. */
const SNAPSHOT_LOADERS = import.meta.glob<{ default: DiscoverySnapshot }>("./data/discovery/*.snapshot.json");

/** Load one real discovery snapshot by dataset id ("discovery:<domain>"). Async: the snapshot is
 *  code-split. Returns null for a non-discovery / unknown id. */
export async function loadDiscovery(id: string): Promise<DiscoverySnapshot | null> {
  const entry = discoveryIndex(id);
  if (!entry) return null;
  const key = `./data/discovery/${entry.domain}.snapshot.json`;
  const loader = SNAPSHOT_LOADERS[key];
  if (!loader) return null;
  const mod = await loader();
  return mod.default;
}
